<template>
  <v-app :class="{ 'theme--dark': isDarkMode }">
    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :mini-variant="miniVariant && !isMobile"
      :expand-on-hover="false"
      :temporary="isMobile"
      :permanent="!isMobile"
      app
      class="sidebar-drawer"
      :color="isDarkMode ? 'sidebar' : 'primary'"
    >
      <!-- Logo Section -->
      <v-list-item class="sidebar-logo px-2 py-1" :class="{ 'justify-center': miniVariant }">
        <v-list-item-avatar v-if="miniVariant" size="32">
          <img :src="logo" alt="Kes Lakay">
        </v-list-item-avatar>
        <template v-else>
          <v-list-item-avatar size="32">
            <img :src="logo" alt="Kes Lakay">
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="sidebar-title">KES LAKAY</v-list-item-title>
          </v-list-item-content>
        </template>
      </v-list-item>

      <!-- User Info -->
      <v-list-item class="sidebar-user py-1" v-if="!miniVariant">
        <v-list-item-avatar size="28" color="secondary">
          <span class="white--text text-caption">{{ getUserInitials() }}</span>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="sidebar-user-name">{{ getAuthenticatedUser() }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="sidebar-divider"></v-divider>

      <!-- Navigation Items -->
      <v-list nav dense class="sidebar-nav">
        <v-list-item
          v-for="item in items"
          :key="item.title"
          link
          :to="item.link"
          class="sidebar-item"
          :active-class="item.renewalsSection ? '' : 'sidebar-item-active'"
          :class="{ 'sidebar-item-active': item.renewalsSection && $route.path.startsWith('/renewals') }"
        >
          <v-list-item-icon>
            <v-icon class="sidebar-icon">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="sidebar-item-title">{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <v-list nav dense>
          <!-- Logout -->
          <v-list-item @click="logout" class="sidebar-item sidebar-logout">
            <v-list-item-icon>
              <v-progress-circular v-if="loading" indeterminate size="24" color="error"></v-progress-circular>
              <v-icon v-else color="error">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="error--text">Déconnexion</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-divider class="sidebar-divider"></v-divider>

          <!-- Collapse Toggle -->
          <v-list-item @click="toggleSidebar" class="sidebar-item sidebar-toggle">
            <v-list-item-icon>
              <v-icon class="sidebar-icon">{{ miniVariant ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="sidebar-item-title">Réduire</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- App Bar -->
    <v-app-bar app flat class="app-header" :color="isDarkMode ? 'surface' : 'white'">
      <!-- Hamburger Menu (mobile only) -->
      <v-btn icon class="hamburger-btn" v-if="isMobile" @click="drawer = !drawer">
        <v-icon>mdi-menu</v-icon>
      </v-btn>
      
      <!-- Mobile Title -->
      <v-toolbar-title v-if="isMobile" class="mobile-title">KES LAKAY</v-toolbar-title>
      
      <v-spacer></v-spacer>

      <!-- Theme Toggle -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon @click="toggleTheme" v-bind="attrs" v-on="on" class="theme-toggle-btn">
            <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>
        </template>
        <span>{{ isDarkMode ? 'Mode clair' : 'Mode sombre' }}</span>
      </v-tooltip>

      <!-- User Menu -->
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" class="user-menu-btn">
            <v-avatar size="36" color="secondary">
              <span class="white--text text-subtitle-2">{{ getUserInitials() }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">{{ getAuthenticatedUser() }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="logout">
            <v-list-item-icon>
              <v-icon color="error" small>mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="error--text">Déconnexion</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Main Content -->
    <v-main class="main-content" :style="{ background: isDarkMode ? '#121212' : '#F5F5F5' }">
      <router-view></router-view>
    </v-main>
  </v-app>
</template>

<script>
import logo from "/src/assets/img/white_emblem.png"

export default {
  data() {
    return {
      drawer: true,
      logo: logo,
      loading: false,
      allItems: [
        { title: 'Dashboard', icon: 'mdi-view-dashboard', link: '/', roles: [1] },
        { title: 'Comptes', icon: 'mdi-domain', link: '/accounts', roles: [1, 2] },
        { title: 'Parrainage', icon: 'mdi-account-cash', link: '/referral', roles: [1] },
        { title: 'Parrains', icon: 'mdi-account-group', link: '/referrers', roles: [1] },
        { title: 'Onboarding', icon: 'mdi-phone-outgoing', link: '/onboarding', roles: [1] },
        { title: 'Appareils', icon: 'mdi-cellphone-link', link: '/subscriptions', roles: [1, 2] },
        { title: 'Souscriptions', icon: 'mdi-autorenew', link: '/renewals/paiements-plan', roles: [1], renewalsSection: true },
        { title: 'Utilisateurs', icon: 'mdi-account-group', link: '/settings', roles: [1] },
        { title: 'Gestion APK', icon: 'mdi-android', link: '/apk', roles: [1, 2] },
        { title: 'Test Natcash', icon: 'mdi-lan-connect', link: '/natcash-test', roles: [1] },
      ],
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters.StateUser;
    },
    userRoleId() {
      return this.currentUser?.role_id || 1;
    },
    items() {
      return this.allItems.filter(item => item.roles.includes(this.userRoleId));
    },
    isDarkMode() {
      return this.$store.getters['theme/isDarkMode'];
    },
    miniVariant() {
      return this.$store.getters['theme/isSidebarMini'];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isTablet() {
      return this.$vuetify.breakpoint.sm;
    }
  },
  watch: {
    isDarkMode: {
      immediate: true,
      handler(val) {
        this.$vuetify.theme.dark = val;
      }
    },
    $route() {
      if (this.isMobile) {
        this.drawer = false;
      }
    }
  },
  methods: {
    async logout() {
      this.loading = true;
      await this.$store.dispatch('LogOut');
      this.$router.push('/login');
    },
    getAuthenticatedUser() {
      const user = this.$store.getters.StateUser;
      return user ? user.full_name : '';
    },
    getUserInitials() {
      const user = this.$store.getters.StateUser;
      if (!user || !user.full_name) return '?';
      const names = user.full_name.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[1][0]).toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    },
    toggleTheme() {
      this.$store.dispatch('theme/toggleTheme');
    },
    toggleSidebar() {
      this.$store.dispatch('theme/toggleSidebar');
    }
  }
};
</script>

<style lang="scss">
.sidebar-drawer {
  transition: all 0.3s ease !important;
  
  .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
    overflow-y: auto !important;
  }
}

.sidebar-logo {
  min-height: 48px !important;
  max-height: 48px !important;
  height: 48px !important;
  
  img {
    border-radius: 6px;
  }
}

.sidebar-title {
  font-size: 14px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.5px;
}

.sidebar-divider {
  opacity: 0.2;
  margin: 2px 12px;
}

.sidebar-user {
  min-height: 40px !important;
  max-height: 40px !important;
  height: 40px !important;
  padding: 4px 12px;
}

.sidebar-user-name {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  font-weight: 500;
}

.sidebar-nav {
  flex: 1;
  padding: 8px !important;
}

.sidebar-item {
  border-radius: 8px !important;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1) !important;
  }
}

.sidebar-item-active {
  background: rgba(246, 141, 58, 0.2) !important;
  
  .sidebar-icon {
    color: #F68D3A !important;
  }
  
  .sidebar-item-title {
    color: #F68D3A !important;
    font-weight: 600;
  }
}

.sidebar-icon {
  color: rgba(255, 255, 255, 0.8) !important;
}

.sidebar-item-title {
  color: rgba(255, 255, 255, 0.9) !important;
  font-size: 14px;
}

.sidebar-logout {
  .sidebar-item-title {
    color: #FF5252 !important;
  }
}

.sidebar-toggle {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.app-header {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.theme-toggle-btn,
.user-menu-btn {
  margin-left: 8px;
}

.main-content {
  min-height: 100vh;
  transition: background 0.3s ease;
}

.v-navigation-drawer--mini-variant {
  .sidebar-item {
    justify-content: center;
  }
}

// Responsive Styles
.hamburger-btn {
  margin-right: 8px;
}

.mobile-title {
  font-size: 16px;
  font-weight: 700;
  color: #003F74;
  letter-spacing: 0.5px;
}

.theme--dark .mobile-title {
  color: #4A90D9;
}

@media (max-width: 959px) {
  .app-header {
    padding: 0 8px !important;
  }
  
  .theme-toggle-btn,
  .user-menu-btn {
    margin-left: 4px;
  }
  
  .user-menu-btn .v-avatar {
    width: 32px !important;
    height: 32px !important;
  }
}

@media (max-width: 599px) {
  .sidebar-drawer {
    width: 280px !important;
  }
  
  .sidebar-user-name {
    font-size: 11px;
  }
  
  .sidebar-item-title {
    font-size: 13px;
  }
}
</style>
